import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Phone, 
  Briefcase, 
  Building2, 
  Wrench, 
  Users, 
  Server, 
  Shield,
  Clock,
  CheckCircle,
  ArrowRight,
  FileText,
  Headphones
} from "lucide-react";
import { QuoteForm } from "@/components/QuoteForm";
import { SEO } from "@/components/SEO";

const BUSINESS_PHONE_LINK = "tel:+33549057490";

export default function ProfessionalServicesPage() {
  useEffect(() => {}, []);

  return (
    <div className="min-h-screen" data-testid="professional-services-page">
      <SEO
        title="Services Informatiques pour Entreprises et Professionnels à Niort"
        description="Solutions informatiques pour entreprises à Niort : maintenance, intervention sur site, sauvegarde, sécurité, formation utilisateurs. Mediastore, partenaire IT des PME, commerces et professionnels en Deux-Sèvres."
        keywords="services informatiques entreprise Niort, maintenance informatique entreprise Niort, contrat maintenance IT Niort, intervention sur site Niort, support informatique Niort, infogérance Niort, sauvegarde données entreprise, sécurité informatique Niort, formation informatique professionnel Niort, partenaire IT Deux-Sèvres"
        path="/services-professionnels"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Services Professionnels", path: "/services-professionnels" },
        ]}
      />
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-purple-50 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-purple-100 text-purple-700 px-4 py-2">
                <Briefcase className="w-4 h-4 mr-2" />
                Solutions B2B
              </Badge>
              
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900">
                Services <span className="text-purple-600">Professionnels</span>
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed">
                Concentrez-vous sur votre métier, nous gérons votre informatique. 
                Maintenance, dépannage, sécurité : des solutions adaptées aux 
                <strong> entreprises et professionnels de Niort</strong>.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href={BUSINESS_PHONE_LINK}>
                  <Button size="lg" className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 h-12 gap-2" data-testid="pro-hero-call-btn">
                    <Phone className="h-5 w-5" />
                    05 49 05 74 90
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-12 gap-2 border-purple-600 text-purple-600 hover:bg-purple-50">
                    Demander un devis
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/h8vphp0g_IMGP9221.png"
                alt="Boutique Mediastore Niort - Services professionnels"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-4 -right-4 bg-purple-600 text-white rounded-xl p-4 shadow-lg hidden md:block">
                <p className="font-bold text-lg">+10 ans</p>
                <p className="text-purple-200 text-sm">d&apos;expérience IT</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-8 bg-white border-y">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Réactivité</p>
                <p className="text-xs text-slate-500">Intervention sous 24-48h</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Shield className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Sécurité</p>
                <p className="text-xs text-slate-500">Données protégées</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Headphones className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Support dédié</p>
                <p className="text-xs text-slate-500">Ligne prioritaire</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">Facturation claire</p>
                <p className="text-xs text-slate-500">Sans surprise</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos offres professionnelles
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des solutions sur mesure pour votre activité
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Contrat Maintenance */}
            <Card className="card-hover" data-testid="pro-service-0">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Wrench className="h-7 w-7 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Contrat de maintenance</CardTitle>
                    <CardDescription className="mt-1">Maintenance préventive et curative de votre parc informatique.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Interventions prioritaires
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Tarifs préférentiels
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Rapport mensuel
                  </li>
                </ul>
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="font-semibold text-purple-600">À partir de 99€/mois</p>
                  <Link to="/contact">
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      En savoir plus
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Intervention sur site */}
            <Card className="card-hover" data-testid="pro-service-1">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-7 w-7 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Intervention sur site</CardTitle>
                    <CardDescription className="mt-1">Nos techniciens se déplacent dans vos locaux.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Niort et 20km
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Délai 24-48h
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Devis gratuit
                  </li>
                </ul>
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="font-semibold text-purple-600">À partir de 79€/intervention</p>
                  <Link to="/contact">
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      En savoir plus
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Sauvegarde & Sécurité */}
            <Card className="card-hover" data-testid="pro-service-2">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Server className="h-7 w-7 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Sauvegarde & Sécurité</CardTitle>
                    <CardDescription className="mt-1">Protection et sauvegarde de vos données professionnelles.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Cloud sécurisé
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Restauration rapide
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Chiffrement
                  </li>
                </ul>
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="font-semibold text-purple-600">Sur devis</p>
                  <Link to="/contact">
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      En savoir plus
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Formation */}
            <Card className="card-hover" data-testid="pro-service-3">
              <CardHeader>
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Users className="h-7 w-7 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">Formation utilisateurs</CardTitle>
                    <CardDescription className="mt-1">Formation à vos outils informatiques et bonnes pratiques.</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Sur site ou en visio
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Supports fournis
                  </li>
                  <li className="flex items-center gap-2 text-slate-600">
                    <CheckCircle className="h-4 w-4 text-purple-600" />
                    Suivi personnalisé
                  </li>
                </ul>
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="font-semibold text-purple-600">À partir de 59€/h</p>
                  <Link to="/contact">
                    <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                      En savoir plus
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nous accompagnons tous les secteurs
            </h2>
            <p className="text-lg text-slate-600">
              Des solutions adaptées à chaque type d&apos;activité
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="text-center p-4 hover:border-purple-300 transition-colors">
              <p className="font-semibold text-slate-900">Commerces</p>
            </Card>
            <Card className="text-center p-4 hover:border-purple-300 transition-colors">
              <p className="font-semibold text-slate-900">Artisans</p>
            </Card>
            <Card className="text-center p-4 hover:border-purple-300 transition-colors">
              <p className="font-semibold text-slate-900">PME / PMI</p>
            </Card>
            <Card className="text-center p-4 hover:border-purple-300 transition-colors">
              <p className="font-semibold text-slate-900">Professions libérales</p>
            </Card>
            <Card className="text-center p-4 hover:border-purple-300 transition-colors">
              <p className="font-semibold text-slate-900">Associations</p>
            </Card>
            <Card className="text-center p-4 hover:border-purple-300 transition-colors">
              <p className="font-semibold text-slate-900">Collectivités</p>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">
              Vous avez des besoins spécifiques ? Parlons-en !
            </p>
            <a href={BUSINESS_PHONE_LINK}>
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700 gap-2">
                <Phone className="h-5 w-5" />
                Contactez-nous
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      {/* FAQ B2B + Tarifs entreprise */}
      <section className="section-padding bg-slate-50" data-testid="pro-faq-section">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-12">
            <Badge className="bg-purple-100 text-purple-700 px-4 py-2 mb-4 hover:bg-purple-100">FAQ B2B</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Questions fréquentes - Services Entreprises
            </h2>
            <p className="text-lg text-slate-600">
              Tout ce que les TPE et PME de Niort et Deux-Sèvres doivent savoir
            </p>
          </div>

          <ol className="space-y-4 list-none p-0">
            {[
              { q: "Quelles entreprises accompagnez-vous à Niort ?", a: "Mediastore Niort est partenaire IT des TPE, PME, commerces, professions libérales, associations et indépendants de Niort, Chauray, Aiffres, Bessines, Magné et toute la Deux-Sèvres." },
              { q: "Proposez-vous un contrat de maintenance informatique ?", a: "Oui. Nos contrats de maintenance préventive + curative démarrent à 59 € HT/poste/mois. Inclus : monitoring, mises à jour, sauvegardes, hotline prioritaire et 1 intervention sur site/mois." },
              { q: "Quel est le délai d'intervention sur site ?", a: "Pour les clients sous contrat : intervention sous 4 heures ouvrées en cas d'urgence, 24 h pour les demandes standard. Sans contrat : intervention sous 48-72 h selon disponibilité." },
              { q: "Travaillez-vous avec des serveurs et du réseau ?", a: "Oui. Installation, configuration et maintenance de serveurs Windows / Linux, switchs, routeurs, points d'accès WiFi, firewalls. Audit réseau et sécurité disponible." },
              { q: "Gérez-vous les sauvegardes de données entreprise ?", a: "Absolument. Mise en place de stratégies de sauvegarde locales (NAS) + cloud (Microsoft 365, Google Workspace, solutions chiffrées). Test régulier de restauration inclus dans nos contrats." },
              { q: "Pouvez-vous former mes utilisateurs ?", a: "Oui, formations en présentiel à Niort ou sur votre site : prise en main Windows/macOS, suite Microsoft 365 (Outlook, Teams, SharePoint), sécurité informatique, sensibilisation phishing." },
              { q: "Facturation et conditions de règlement ?", a: "Devis transparent, facturation mensuelle pour les contrats. Paiement à 30 jours nets pour les entreprises. TVA récupérable. Carte bancaire, virement, chèque acceptés." },
              { q: "Êtes-vous éligibles aux aides régionales ?", a: "Oui. Nos prestations de transformation numérique peuvent être éligibles aux dispositifs « Chèque numérique » Nouvelle-Aquitaine et au crédit d'impôt formation dirigeants. Renseignez-vous." },
            ].map((item, i) => (
              <li key={item.q} className="bg-white rounded-xl p-6 hover:bg-purple-50 transition-colors shadow-sm" data-testid={`pro-faq-${i}`}>
                <h3 className="font-bold text-lg text-slate-900 mb-2 flex gap-3">
                  <span className="text-purple-600 flex-shrink-0">{i + 1}.</span>
                  <span>{item.q}</span>
                </h3>
                <p className="text-slate-700 leading-relaxed pl-7">{item.a}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Tableau tarifs B2B */}
      <section className="section-padding bg-white" data-testid="pro-pricing-section">
        <div className="container-custom max-w-5xl">
          <div className="text-center mb-10">
            <Badge className="bg-slate-900 text-white px-4 py-2 mb-4 hover:bg-slate-900">Tarifs B2B</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Grille tarifaire entreprises
            </h2>
            <p className="text-lg text-slate-600">
              Tarifs HT - remises pour les contrats annuels
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl shadow-lg overflow-hidden border border-slate-200">
            <table className="w-full">
              <caption className="sr-only">Tarifs services informatiques pour entreprises Niort</caption>
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th scope="col" className="text-left p-4 font-semibold">Prestation</th>
                  <th scope="col" className="text-center p-4 font-semibold">Tarif HT</th>
                  <th scope="col" className="text-center p-4 font-semibold">Unité</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Audit informatique complet", "Gratuit", "1 audit / entreprise"],
                  ["Contrat maintenance préventive", "59 €", "/ poste / mois"],
                  ["Intervention sur site Niort", "89 €", "/ heure"],
                  ["Intervention sur site < 20 km", "99 €", "/ heure"],
                  ["Support à distance (TeamViewer)", "65 €", "/ heure"],
                  ["Installation poste de travail", "145 €", "Forfait"],
                  ["Configuration serveur", "Sur devis", "Selon scope"],
                  ["Sauvegarde cloud sécurisée", "À partir de 19 €", "/ utilisateur / mois"],
                  ["Formation utilisateurs", "À partir de 419 €", "/ demi-journée"],
                ].map((row) => (
                  <tr key={row[0]} className="border-t border-slate-100 hover:bg-purple-50 transition-colors">
                    <td className="p-4 font-medium text-slate-900">{row[0]}</td>
                    <td className="p-4 text-center text-purple-600 font-semibold">{row[1]}</td>
                    <td className="p-4 text-center text-slate-700">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="grid md:grid-cols-2 gap-3 mt-8 text-slate-700">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /> Engagement annuel : -10 % sur la maintenance</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /> Plus de 10 postes : remise négociée</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /> Facturation mensuelle, paiement à 30 j</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-purple-600 mt-1 flex-shrink-0" /> TVA 20 % récupérable</li>
          </ul>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Demandez votre audit gratuit
            </h2>
            <p className="text-lg text-slate-600">
              Nous analysons vos besoins et vous proposons la solution adaptée
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>
    </div>
  );
}
